function getByPath(root, path) {
  return String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), root)
}

function setByPath(root, path, value) {
  const keys = String(path || '').split('.').filter(Boolean)
  if (!keys.length) return
  let cursor = root
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i]
    if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {}
    cursor = cursor[key]
  }
  cursor[keys[keys.length - 1]] = value
}

function presetRowRank(row) {
  const severityWeight = row.severity === 'RISK' ? 300 : row.severity === 'WATCH' ? 200 : 100
  const kindWeight = row.kind === 'overview'
    ? 40
    : row.kind === 'family'
      ? 30
      : row.kind === 'decisionModule'
        ? 20
        : 10
  const scopeWeight = row.preset && row.preset.scope === 'SIGNAL_ENTRY'
    ? 15
    : row.preset && row.preset.scope === 'INDICATOR_PARAMS'
      ? 12
      : 5
  const patchWeight = Math.min(9, Array.isArray(row.preset && row.preset.patches) ? row.preset.patches.length : 0)
  const uniquePathWeight = Math.min(6, uniquePatchPathCount(row.preset))
  const directActionWeight = hasOnlyEnablePatch(row.preset) ? 0 : 8
  return severityWeight + kindWeight + scopeWeight + patchWeight + uniquePathWeight + directActionWeight
}

function presetRowReason(row) {
  const severityText = row.severity === 'RISK'
    ? 'risk baskisi yuksek'
    : row.severity === 'WATCH'
      ? 'dikkat gerektiren outcome var'
      : 'bilgilendirici guidance var'
  const kindText = row.kind === 'overview'
    ? 'genel outcome ozeti'
    : row.kind === 'family'
      ? 'family davranisi'
      : row.kind === 'decisionModule'
        ? 'gate/modul davranisi'
        : 'guidance agirligi'
  const scopeText = row.preset && row.preset.scope === 'GLOBAL_EXPOSURE'
    ? 'exposure ayarina dokunuyor'
    : row.preset && row.preset.scope === 'INDICATOR_PARAMS'
      ? 'indicator engine ayarina dokunuyor'
      : 'signal entry ayarina dokunuyor'
  return `${severityText}; ${kindText}; ${scopeText}.`
}

function presetSignature(preset) {
  const patches = Array.isArray(preset && preset.patches) ? preset.patches : []
  return patches
    .map(patch => `${patch.path}:${patch.op}:${String(patch.value)}`)
    .sort()
    .join('|')
}

function uniquePatchPathCount(preset) {
  const patches = Array.isArray(preset && preset.patches) ? preset.patches : []
  return new Set(patches.map(patch => patch.path)).size
}

function hasOnlyEnablePatch(preset) {
  const patches = Array.isArray(preset && preset.patches) ? preset.patches : []
  return patches.length === 1 && patches[0].op === 'SET' && patches[0].value === true
}

function patchPathLabel(path) {
  if (path === 'signalEntry.minEntryScore') return 'Min score'
  if (path === 'signalEntry.minRemainingRewardRisk') return 'Min RR'
  if (path === 'signalEntry.confirmationTicks') return 'Onay bekleme'
  if (path === 'signalEntry.lateSignalPenaltyStartPct') return 'Geç sinyal cezası'
  if (path === 'signalEntry.familyMaxWeight') return 'Family ağırlığı'
  if (path === 'indicatorParams.momentumQualityEnabled') return 'Momentum kalite'
  if (path === 'indicatorParams.minMomentumBodyRatio') return 'Min gövde'
  if (path === 'indicatorParams.minMomentumCloseLocation') return 'Min kapanış konumu'
  if (path === 'positionManagement.globalExposure.enabled') return 'Global exposure'
  if (path === 'positionManagement.globalExposure.blockOppositeSide') return 'Ters yön blok'
  return String(path || '')
}

function patchValueLabel(patch) {
  if (!patch) return ''
  if (patch.op === 'SET') return patch.value === true ? 'aç' : patch.value === false ? 'kapat' : String(patch.value)
  const value = Number(patch.value || 0)
  if (Number.isFinite(value)) return `${value >= 0 ? '+' : ''}${String(value).replace('.', ',')}`
  return String(patch.value)
}

function presetActionSummary(preset) {
  const patches = Array.isArray(preset && preset.patches) ? preset.patches : []
  return patches
    .slice(0, 3)
    .map(patch => `${patchPathLabel(patch.path)} ${patchValueLabel(patch)}`)
    .join(' · ')
}

function compactRecommendation(text) {
  const value = String(text || '').trim()
  if (!value) return ''
  return value.length > 120 ? `${value.slice(0, 120)}...` : value
}

export function extractEntryPresetRows(summary, limit = 6) {
  const prescriptions = (summary && summary.prescriptions) || []
  const rows = prescriptions.flatMap(item => (((item.meta && item.meta.presets) || []).map(preset => ({
    prescriptionId: item.id,
    severity: item.severity,
    kind: item.meta && item.meta.kind,
    title: item.title,
    recommendation: item.recommendation,
    preset,
  }))))
  const uniqueRows = []
  const seen = new Set()
  rows
    .filter(row => Array.isArray(row.preset && row.preset.patches) && row.preset.patches.length > 0)
    .sort((a, b) => presetRowRank(b) - presetRowRank(a) || String(a.preset && a.preset.label || '').localeCompare(String(b.preset && b.preset.label || '')))
    .forEach(row => {
      const signature = `${row.preset && row.preset.scope}:${presetSignature(row.preset)}`
      if (seen.has(signature)) return
      seen.add(signature)
      uniqueRows.push(row)
    })
  return uniqueRows
    .map((row, index) => ({
      ...row,
      rank: index + 1,
      priorityReason: presetRowReason(row),
      actionSummary: presetActionSummary(row.preset),
      recommendationShort: compactRecommendation(row.recommendation),
      patchCount: Array.isArray(row.preset && row.preset.patches) ? row.preset.patches.length : 0,
    }))
    .slice(0, limit)
}

export function applyEntryQualityPreset(configRoot, preset) {
  if (!configRoot || !preset || !Array.isArray(preset.patches)) return configRoot
  preset.patches.forEach(patch => {
    const path = patch && patch.path
    if (!path) return
    if (patch.op === 'SET') {
      setByPath(configRoot, path, patch.value)
      return
    }
    const current = Number(getByPath(configRoot, path) || 0)
    const delta = Number(patch.value || 0)
    const next = Number.isFinite(current) ? current + delta : delta
    setByPath(configRoot, path, next)
  })
  return configRoot
}
